import { useEffect, useState } from "react";
import { fmtBRL } from "./PlanPicker";

/** Catálogo de planos (mesmo shape do backend planStore/planCatalog). */
export interface AssistantPlan {
  id: string;
  name: string;
  priceBrlMonthly: number;
  seatLimit: number;
  workspaceLimit: number;
  repoLimit: number;
  budgetUsdMonthly: number;
  apiCallsMonthly: number;
  /** SEMPRE derivados dos limites no backend - aqui é só leitura. */
  highlights: string[];
}

type PlanDraft = Omit<AssistantPlan, "highlights">;

const EMPTY_NEW: PlanDraft = {
  id: "",
  name: "",
  priceBrlMonthly: 0,
  seatLimit: 5,
  workspaceLimit: 5,
  repoLimit: 5,
  budgetUsdMonthly: 20,
  apiCallsMonthly: 500,
};

/**
 * Espelho client-side da derivação de destaques do backend (planHighlights) -
 * usado só como PRÉVIA ao vivo enquanto se edita; a fonte canônica é o servidor.
 */
function previewHighlights(p: PlanDraft): string[] {
  const n = (v: number) => (Number.isFinite(v) ? v.toLocaleString("pt-BR") : "0");
  const plural = (v: number, s: string, pl: string) => `${n(v)} ${v === 1 ? s : pl}`;
  return [
    plural(p.seatLimit, "usuário", "usuários"),
    plural(p.repoLimit, "repositório", "repositórios"),
    plural(p.workspaceLimit, "workspace", "workspaces"),
    `US$ ${n(p.budgetUsdMonthly)}/mês de IA`,
    `API: ${n(p.apiCallsMonthly)} req/mês`,
  ];
}

/** Rótulos amigáveis dos limites (ordem de exibição nos cards). */
const LIMIT_FIELDS: Array<{ key: keyof PlanDraft; label: string; hint?: string; step?: string }> = [
  { key: "seatLimit", label: "Usuários" },
  { key: "repoLimit", label: "Repositórios" },
  { key: "workspaceLimit", label: "Workspaces" },
  { key: "budgetUsdMonthly", label: "Orçamento de IA (US$/mês)", step: "any" },
  { key: "apiCallsMonthly", label: "API (req/mês)" },
];

/**
 * Catálogo de planos (só sysadmin). Os limites valem de verdade (assentos,
 * workspaces, repositórios, orçamento de IA e API) e os destaques exibidos no
 * cadastro/gate são SEMPRE derivados deles - nada de texto manual.
 */
/** Tempo padrão do período de teste - vive aqui, junto do catálogo de planos. */
function TrialDefaultBlock() {
  const [trialDays, setTrialDays] = useState<number>(14);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/admin/platform-defaults")
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { trialDays?: number } | null) => {
        if (j && typeof j.trialDays === "number") setTrialDays(j.trialDays);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const r = await fetch("/api/admin/platform-defaults", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ trialDays }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(j.error ?? "falha ao salvar");
        return;
      }
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;
  return (
    <div className="admin-block" style={{ marginBottom: 16 }}>
      <h3 style={{ marginBottom: 4 }}>Período de teste (trial)</h3>
      <p className="admin-hint" style={{ marginBottom: 10 }}>
        Dias grátis de um tenant novo. Vale pros próximos cadastros e alimenta os textos do site e do
        app ("X dias grátis"). Durante o teste, a organização fica limitada ao menor plano do catálogo.
      </p>
      {error && <div className="auth-error" style={{ marginBottom: 8 }}>{error}</div>}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label className="field" style={{ width: 140 }}>
          <span>Dias grátis</span>
          <input
            type="number"
            min={0}
            max={365}
            value={trialDays}
            onChange={(e) => {
              setTrialDays(Number(e.target.value));
              setSaved(false);
            }}
          />
        </label>
        <button className="btn-approve" type="button" disabled={saving} onClick={() => void save()}>
          {saving ? "Salvando…" : saved ? "Salvo!" : "Salvar"}
        </button>
      </div>
    </div>
  );
}

/** Modelo da geração de docs (sysadmin) - tarefa volumosa num modelo econômico. */
function DocGenModelBlock() {
  const [model, setModel] = useState<string>("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/admin/platform-defaults")
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { docGenModel?: string } | null) => {
        if (j && typeof j.docGenModel === "string") setModel(j.docGenModel);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const r = await fetch("/api/admin/platform-defaults", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ docGenModel: model }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(j.error ?? "falha ao salvar");
        return;
      }
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;
  return (
    <div className="admin-block" style={{ marginBottom: 16 }}>
      <h3 style={{ marginBottom: 4 }}>Modelo da geração de documentação</h3>
      <p className="admin-hint" style={{ marginBottom: 10 }}>
        Modelo de IA usado pra gerar/atualizar as docs automáticas dos repositórios (tarefa volumosa -
        o padrão é o Haiku, econômico). Vale a partir da próxima geração, sem reiniciar.
      </p>
      {error && <div className="auth-error" style={{ marginBottom: 8 }}>{error}</div>}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label className="field" style={{ width: 280 }}>
          <span>Modelo</span>
          <select
            value={model}
            onChange={(e) => {
              setModel(e.target.value);
              setSaved(false);
            }}
          >
            <option value="">Padrão (Haiku 4.5 - econômico)</option>
            <option value="claude-haiku-4-5">Haiku 4.5 (econômico)</option>
            <option value="claude-sonnet-4-6">Sonnet 4.6 (equilíbrio)</option>
            <option value="claude-opus-4-8">Opus 4.8 (mais capaz, mais caro)</option>
            <option value="claude-fable-5">Fable 5</option>
          </select>
        </label>
        <button className="btn-approve" type="button" disabled={saving} onClick={() => void save()}>
          {saving ? "Salvando…" : saved ? "Salvo!" : "Salvar"}
        </button>
      </div>
    </div>
  );
}

export function PlansPanel() {
  const [plans, setPlans] = useState<AssistantPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/admin/plans");
      const j = (await r.json().catch(() => ({}))) as { plans?: AssistantPlan[]; error?: string };
      if (!r.ok) {
        setError(j.error ?? "falha ao carregar os planos");
        return;
      }
      setPlans(j.plans ?? []);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="admin-body">
      <section className="admin-col" style={{ gridColumn: "1 / -1" }}>
        <TrialDefaultBlock />
      <DocGenModelBlock />
        <div className="admin-block">
          <h2>Planos</h2>
          <p className="admin-hint">
            Os limites configurados aqui valem de verdade (assentos, workspaces, repositórios,
            orçamento de IA e chamadas de API) e os destaques exibidos no cadastro saem deles
            automaticamente. Organizações são movidas de plano em Organizações → Gerenciar.
          </p>
          {error && <div className="auth-error">{error}</div>}
          {loading ? (
            <p className="admin-user-meta">Carregando…</p>
          ) : (
            <div className="plans-grid">
              {plans.map((p) => (
                <PlanCard key={p.id} plan={p} onChanged={load} setError={setError} />
              ))}
              {creating ? (
                <PlanCard
                  plan={{ ...EMPTY_NEW, highlights: previewHighlights(EMPTY_NEW) }}
                  isNew
                  startEditing
                  onChanged={() => {
                    setCreating(false);
                    void load();
                  }}
                  onCancel={() => setCreating(false)}
                  setError={setError}
                />
              ) : (
                <button className="plan-card plan-card-new" type="button" onClick={() => setCreating(true)}>
                  <span className="plan-card-new-plus" aria-hidden>
                    +
                  </span>
                  Novo plano
                </button>
              )}
            </div>
          )}
        </div>
        <AddonCatalogBlock />
      </section>
    </div>
  );
}

/** Tipos de pacote adicional - espelham os limites do plano. */
type CatalogKind = "repos" | "ai_budget" | "api_calls" | "seats" | "workspaces";

const CATALOG_KIND_PT: Record<CatalogKind, string> = {
  repos: "Repositórios extras",
  ai_budget: "Orçamento de IA (US$)",
  api_calls: "API (req/mês)",
  seats: "Usuários extras",
  workspaces: "Workspaces extras",
};

const CATALOG_QTY_LABEL: Record<CatalogKind, string> = {
  repos: "Repositórios",
  ai_budget: "US$ por mês",
  api_calls: "Req/mês",
  seats: "Usuários",
  workspaces: "Workspaces",
};

function fmtCatalogQty(kind: CatalogKind, qty: number): string {
  switch (kind) {
    case "repos":
      return `+${qty}`;
    case "ai_budget":
      return `+US$ ${qty.toFixed(2)}/mês`;
    case "api_calls":
      return `+${qty.toLocaleString("pt-BR")} req/mês`;
    case "seats":
      return `+${qty}`;
    case "workspaces":
      return `+${qty}`;
  }
}

/** Pacote do catálogo global de adicionais (mesmo shape do backend addonPackageStore). */
interface CatalogPackage {
  id: string;
  kind: CatalogKind;
  qty: number;
  priceBrl: number;
  label: string;
  active: boolean;
  sortOrder: number;
}

type CatalogDraft = Omit<CatalogPackage, "id">;

const EMPTY_PKG: CatalogDraft = { kind: "repos", qty: 5, priceBrl: 49.9, label: "", active: true, sortOrder: 0 };

/**
 * Catálogo GLOBAL de pacotes adicionais (repos extras / orçamento de IA) - é
 * daqui que o admin das organizações contrata (sempre com cobrança, liberação
 * após o pagamento). CRUD só do sysadmin; mudanças valem na hora.
 */
function AddonCatalogBlock() {
  const [items, setItems] = useState<CatalogPackage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<CatalogDraft | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDelId, setConfirmDelId] = useState<string | null>(null);

  const load = async () => {
    const r = await fetch("/api/admin/addon-packages");
    const j = (await r.json().catch(() => ({}))) as { packages?: CatalogPackage[]; error?: string };
    if (r.ok) setItems(j.packages ?? []);
    else setError(j.error ?? "falha ao carregar o catálogo de pacotes");
  };

  useEffect(() => {
    void load();
  }, []);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const url = editingId ? `/api/admin/addon-packages/${encodeURIComponent(editingId)}` : "/api/admin/addon-packages";
      const r = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(draft),
      });
      const j = (await r.json().catch(() => ({}))) as { packages?: CatalogPackage[]; error?: string };
      if (!r.ok) {
        setError(j.error ?? "falha ao salvar o pacote");
        return;
      }
      setItems(j.packages ?? []);
      setDraft(null);
      setEditingId(null);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`/api/admin/addon-packages/${encodeURIComponent(id)}`, { method: "DELETE" });
      const j = (await r.json().catch(() => ({}))) as { packages?: CatalogPackage[]; error?: string };
      if (!r.ok) {
        setError(j.error ?? "falha ao remover o pacote");
        return;
      }
      setItems(j.packages ?? []);
      setConfirmDelId(null);
    } finally {
      setBusy(false);
    }
  };

  const form = draft && (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
      <label className="field" style={{ width: 190 }}>
        <span>Tipo</span>
        <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as CatalogKind })}>
          {(Object.keys(CATALOG_KIND_PT) as CatalogKind[]).map((k) => (
            <option key={k} value={k}>
              {CATALOG_KIND_PT[k]}
            </option>
          ))}
        </select>
      </label>
      <label className="field" style={{ width: 130 }}>
        <span>{CATALOG_QTY_LABEL[draft.kind]}</span>
        <input
          type="number"
          min={draft.kind === "ai_budget" ? 0.01 : 1}
          step={draft.kind === "ai_budget" ? 0.01 : 1}
          value={draft.qty}
          onChange={(e) => setDraft({ ...draft, qty: Number(e.target.value) })}
        />
      </label>
      <label className="field" style={{ width: 130 }}>
        <span>Preço (R$)</span>
        <input type="number" min={0.01} step="0.01" value={draft.priceBrl} onChange={(e) => setDraft({ ...draft, priceBrl: Number(e.target.value) })} />
      </label>
      <label className="field" style={{ flex: "1 1 200px" }}>
        <span>Nome (opcional - derivado se vazio)</span>
        <input type="text" value={draft.label} placeholder="ex.: Pacote 10 repositórios" onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
      </label>
      <label className="field" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 6, width: "auto" }}>
        <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
        <span>Ativo (visível para contratação)</span>
      </label>
      <button className="btn-approve" type="button" disabled={busy} onClick={() => void save()}>
        {editingId ? "Salvar pacote" : "Criar pacote"}
      </button>
      <button
        className="btn-reject"
        type="button"
        disabled={busy}
        onClick={() => {
          setDraft(null);
          setEditingId(null);
        }}
      >
        Cancelar
      </button>
    </div>
  );

  return (
    <div className="admin-block" style={{ marginTop: 16 }}>
      <h2>Pacotes adicionais (catálogo)</h2>
      <p className="admin-hint">
        Pacotes que o administrador de qualquer organização pode contratar por cima do plano: repositórios
        extras e orçamento de IA. A contratação sempre gera cobrança e o pacote só é liberado depois que o
        pagamento é aprovado. O sysadmin também aplica estes pacotes (ou avulsos/cortesia) em Organizações →
        Gerenciar → Plano e billing.
      </p>
      {error && <div className="auth-error" style={{ marginBottom: 8 }}>{error}</div>}
      {form}
      {!draft && (
        <button className="btn-approve" type="button" style={{ marginBottom: 12 }} onClick={() => setDraft({ ...EMPTY_PKG })}>
          Novo pacote
        </button>
      )}
      {items.length === 0 ? (
        <div className="admin-empty small">Nenhum pacote no catálogo ainda.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="admin-table" style={{ width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Pacote</th>
                <th style={{ textAlign: "left" }}>Tipo</th>
                <th style={{ textAlign: "right" }}>Quantidade</th>
                <th style={{ textAlign: "right" }}>Preço</th>
                <th style={{ textAlign: "left" }}>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} style={!p.active ? { opacity: 0.55 } : undefined}>
                  <td style={{ fontWeight: 600 }}>{p.label}</td>
                  <td>{CATALOG_KIND_PT[p.kind]}</td>
                  <td style={{ textAlign: "right" }}>{fmtCatalogQty(p.kind, p.qty)}</td>
                  <td style={{ textAlign: "right" }}>{fmtBRL(p.priceBrl)}</td>
                  <td>
                    <span className={`badge ${p.active ? "ok" : ""}`}>{p.active ? "ativo" : "inativo"}</span>
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      className="btn-approve"
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setEditingId(p.id);
                        setDraft({ kind: p.kind, qty: p.qty, priceBrl: p.priceBrl, label: p.label, active: p.active, sortOrder: p.sortOrder });
                        setConfirmDelId(null);
                      }}
                    >
                      Editar
                    </button>{" "}
                    {confirmDelId === p.id ? (
                      <>
                        <button className="btn-reject" type="button" disabled={busy} onClick={() => void remove(p.id)}>
                          Confirmar remoção
                        </button>{" "}
                        <button className="btn-approve" type="button" disabled={busy} onClick={() => setConfirmDelId(null)}>
                          Voltar
                        </button>
                      </>
                    ) : (
                      <button className="btn-reject" type="button" disabled={busy} onClick={() => setConfirmDelId(p.id)}>
                        Remover
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PlanCard({
  plan,
  isNew = false,
  startEditing = false,
  onChanged,
  onCancel,
  setError,
}: {
  plan: AssistantPlan;
  isNew?: boolean;
  startEditing?: boolean;
  onChanged: () => void;
  onCancel?: () => void;
  setError: (e: string | null) => void;
}) {
  const [editing, setEditing] = useState(startEditing);
  const [draft, setDraft] = useState<PlanDraft>({ ...plan });
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const set = (patch: Partial<PlanDraft>) => setDraft((d) => ({ ...d, ...patch }));

  const closeEdit = () => {
    setEditing(false);
    setConfirmDel(false);
    setDraft({ ...plan });
    onCancel?.();
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const url = isNew ? "/api/admin/plans" : `/api/admin/plans/${encodeURIComponent(plan.id)}`;
      const r = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(j.error ?? "falha ao salvar o plano");
        return;
      }
      setEditing(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`/api/admin/plans/${encodeURIComponent(plan.id)}`, { method: "DELETE" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(j.error ?? "falha ao remover o plano");
        return;
      }
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const shown = editing ? previewHighlights(draft) : plan.highlights;

  return (
    <div className={`plan-card ${editing ? "editing" : ""}`}>
      <div className="plan-card-head">
        {editing ? (
          <div className="plan-card-head-fields">
            {isNew && (
              <label className="auth-field">
                <span>Identificador (id)</span>
                <input
                  type="text"
                  value={draft.id}
                  placeholder="ex.: assistant_starter"
                  onChange={(e) => set({ id: e.target.value })}
                  autoFocus
                />
              </label>
            )}
            <label className="auth-field">
              <span>Nome do plano</span>
              <input type="text" value={draft.name} placeholder="Ex.: Essencial" onChange={(e) => set({ name: e.target.value })} />
            </label>
            <label className="auth-field">
              <span>Preço (R$/mês)</span>
              <input
                type="number"
                min={0}
                step="any"
                value={draft.priceBrlMonthly}
                onChange={(e) => set({ priceBrlMonthly: Number(e.target.value) })}
              />
            </label>
          </div>
        ) : (
          <>
            <div className="plan-card-title">
              <h3>{plan.name}</h3>
              <code className="plan-card-id">{plan.id}</code>
            </div>
            <div className="plan-card-price">
              {fmtBRL(plan.priceBrlMonthly)}
              <span>/mês</span>
            </div>
          </>
        )}
      </div>

      {editing ? (
        <div className="plan-card-limits-form">
          {LIMIT_FIELDS.map((f) => (
            <label key={f.key} className="auth-field">
              <span>{f.label}</span>
              <input
                type="number"
                min={0}
                step={f.step ?? 1}
                value={draft[f.key] as number}
                onChange={(e) => set({ [f.key]: Number(e.target.value) } as Partial<PlanDraft>)}
              />
            </label>
          ))}
        </div>
      ) : null}

      <ul className="plan-card-highlights" aria-label="O que está incluído">
        {shown.map((h) => (
          <li key={h}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M3 8.5l3.2 3L13 4.5" />
            </svg>
            {h}
          </li>
        ))}
      </ul>
      {editing && <p className="plan-card-derived-note">Os destaques acima são gerados automaticamente a partir dos limites.</p>}

      <div className="plan-card-actions">
        {editing ? (
          <>
            <button className="btn-approve" type="button" disabled={busy} onClick={() => void save()}>
              {busy ? "Salvando…" : isNew ? "Criar plano" : "Salvar"}
            </button>
            <button className="btn-reject" type="button" disabled={busy} onClick={closeEdit}>
              Cancelar
            </button>
            {!isNew &&
              (confirmDel ? (
                <button className="btn-reject danger" type="button" disabled={busy} onClick={() => void remove()}>
                  Confirmar remoção
                </button>
              ) : (
                <button className="plan-card-remove" type="button" disabled={busy} onClick={() => setConfirmDel(true)}>
                  Remover plano
                </button>
              ))}
          </>
        ) : (
          <button className="btn-approve" type="button" onClick={() => setEditing(true)}>
            Editar
          </button>
        )}
      </div>
    </div>
  );
}
